import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Loader2, Landmark, Send, RefreshCw, Trash2, Pencil, Save, X, FilePlus2, Eye, Download, Paperclip, MailCheck,
} from 'lucide-react';
import { fmtEUR, fmtDate } from './sepaHelpers';
import { MandatPdfApercu, telechargerMandat } from './sepa/MandatPdfApercu';
import { MandatDocuments } from './sepa/MandatDocuments';
import { NettoyageEssais } from './sepa/NettoyageEssais';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISPOSITIFS = ['RCDS', 'DOMIUM', 'CMI', 'GFA'];
const LIBELLES = {
  RCDS: ['RCDS BATICO ANNUELLE', 'RCDS BATICO NOMINATIVE', 'RCDS BATICO AVENANT', 'RCDS BATICO ARCHOS'],
  DOMIUM: ['DOS DOMIUM'], CMI: ['CMI'], GFA: ['VEFA'],
};
const EMPTY_FORM = {
  membre_nom: '', raison_sociale: '', email: '', telephone: '', dispositif: 'RCDS', libelle_dispositif: '',
  montant_eur: '', acompte_eur: '', nb_echeances: 4, intervalle_jours: 30, premiere_date: '', membre_id: '',
};

/** Création manuelle de mandats SEPA (signature Stripe + archivage GED) + config impayés. */
export default function SepaMandateCreationPanel() {
  const { getAuthHeader } = useAuth();
  const [mandats, setMandats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', q: '', date_from: '', date_to: '', famille: '', precis: '' });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [cfg, setCfg] = useState({ frais_eur: 60, relance_j_plus: 7 });
  const [cfgBusy, setCfgBusy] = useState(false);
  const [membres, setMembres] = useState([]);
  const [apercu, setApercu] = useState(null);
  const [docsMandat, setDocsMandat] = useState(null);

  const headers = useCallback(() => ({ headers: getAuthHeader() }), [getAuthHeader]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.famille) params.set('famille', filters.famille);
      if (filters.precis) params.set('precis', filters.precis);
      const res = await axios.get(`${API}/sepa-stripe/admin/mandats?${params}`, headers());
      setMandats(res.data?.mandats || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement mandats');
    } finally {
      setLoading(false);
    }
  }, [filters, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    axios.get(`${API}/sepa-stripe/admin/config-impayes`, headers())
      .then((r) => setCfg({ frais_eur: r.data?.frais_eur ?? 60, relance_j_plus: r.data?.relance_j_plus ?? 7 }))
      .catch(() => {});
    axios.get(`${API}/cooper/membres/liste`, headers())
      .then((r) => setMembres(r.data?.membres || []))
      .catch(() => {});
  }, [headers]);

  const saveCfg = async () => {
    setCfgBusy(true);
    try {
      await axios.put(`${API}/sepa-stripe/admin/config-impayes`, cfg, headers());
      toast.success('Configuration impayés enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement config');
    } finally {
      setCfgBusy(false);
    }
  };

  const pickMembre = (id) => {
    const mb = membres.find((x) => (x.id || x._id) === id);
    if (!mb) return;
    setForm((f) => ({
      ...f,
      membre_id: id,
      membre_nom: mb.nom_complet || `${mb.prenom || ''} ${mb.nom || ''}`.trim() || mb.raison_sociale || '',
      raison_sociale: mb.raison_sociale || '',
      email: mb.email || '',
      telephone: mb.telephone || '',
    }));
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      membre_nom: m.membre_nom || '', raison_sociale: m.raison_sociale || '',
      email: m.email || '', telephone: m.telephone || '', dispositif: m.dispositif || 'RCDS',
      libelle_dispositif: m.libelle_dispositif || '',
      montant_eur: (m.montant_cents || 0) / 100, acompte_eur: (m.acompte_cents || 0) / 100,
      nb_echeances: m.nb_echeances || 4, intervalle_jours: m.intervalle_jours || 30,
      premiere_date: m.premiere_date || '', membre_id: m.membre_id || '',
    });
    setFormOpen(true);
  };

  const submit = async () => {
    if (!form.membre_nom || !form.email || !form.montant_eur) {
      toast.error('Nom, email et montant sont requis');
      return;
    }
    setBusy('form');
    try {
      if (editId) {
        await axios.put(`${API}/sepa-stripe/admin/mandats/${editId}`, form, headers());
        toast.success('Mandat mis à jour');
      } else {
        const r = await axios.post(`${API}/sepa-stripe/admin/mandats`, form, headers());
        toast.success('Mandat créé — lien de signature prêt');
        if (r.data?.mandat?.lien_signature_url || r.data?.mandat?.stripe_setup_url) {
          window.navigator.clipboard?.writeText(r.data.mandat.lien_signature_url || r.data.mandat.stripe_setup_url).catch(() => {});
          toast.info('Lien de signature copié dans le presse-papiers');
        }
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      setEditId(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement mandat');
    } finally {
      setBusy(null);
    }
  };

  const doAction = async (m, action) => {
    setBusy(m.id);
    try {
      if (action === 'resend') {
        await axios.post(`${API}/sepa-stripe/admin/mandats/${m.id}/resend`, {}, headers());
        toast.success('Lien de signature renvoyé au membre');
      } else if (action === 'renvoyer-signe') {
        await axios.post(`${API}/sepa-stripe/admin/mandats/${m.id}/renvoyer-signe`, {}, headers());
        toast.success('Mandat signé renvoyé au membre par e-mail');
      } else if (action === 'refresh') {
        const r = await axios.post(`${API}/sepa-stripe/admin/mandats/${m.id}/refresh`, {}, headers());
        toast.success(r.data?.mandat?.status === 'signe' ? 'Mandat signé ✓' : 'Statut rafraîchi');
      } else if (action === 'delete') {
        if (!window.confirm(`Supprimer le mandat ${m.rum} ?`)) { setBusy(null); return; }
        await axios.delete(`${API}/sepa-stripe/admin/mandats/${m.id}`, headers());
        toast.success('Mandat supprimé');
      }
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#0a3d62]/15 bg-white shadow-sm" data-testid="sepa-mandate-creation">
      <div className="bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center">
            <Landmark className="h-5 w-5 text-[#f5cc3e]" />
          </div>
          <div>
            <h3 className="text-white font-bold">Création de mandats SEPA</h3>
            <p className="text-[11px] text-white/60">RCDS · DOMIUM · CMI · GFA — signature Stripe + archivage GED</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] text-white/80 bg-white/10 rounded-lg px-2 py-1.5">
            <Save className="h-3 w-3" /> Frais d&apos;impayé (€)
          </span>
          <Input
            type="number" min="0" value={cfg.frais_eur}
            onChange={(e) => setCfg((c) => ({ ...c, frais_eur: Number(e.target.value) }))}
            className="w-20 h-8 bg-white text-slate-800" data-testid="sepa-cfg-frais"
          />
          <span className="text-[11px] text-white/80">J+</span>
          <Input
            type="number" min="1" value={cfg.relance_j_plus}
            onChange={(e) => setCfg((c) => ({ ...c, relance_j_plus: Number(e.target.value) }))}
            className="w-16 h-8 bg-white text-slate-800" data-testid="sepa-cfg-jplus"
          />
          <Button size="sm" onClick={saveCfg} disabled={cfgBusy} className="bg-white/15 hover:bg-white/25 text-white border border-white/25" data-testid="sepa-cfg-save">
            {cfgBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </Button>
          <Button
            size="sm"
            onClick={() => { setForm(EMPTY_FORM); setEditId(null); setFormOpen(true); }}
            className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold"
            data-testid="sepa-mandat-create-open"
          >
            <FilePlus2 className="h-4 w-4 mr-1.5" /> Créer un mandat SEPA
          </Button>
          <NettoyageEssais onDone={fetchAll} />
        </div>
      </div>

      {formOpen && (
        <div className="border-b border-slate-200 bg-slate-50/70 p-4 space-y-3" data-testid="sepa-mandat-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#0a3d62]">{editId ? 'Modifier le mandat' : 'Nouveau mandat SEPA'}</p>
            <button type="button" onClick={() => setFormOpen(false)} data-testid="sepa-mandat-form-close"><X className="h-4 w-4 text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <select
                className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white mb-1"
                onChange={(e) => pickMembre(e.target.value)}
                value={form.membre_id}
                data-testid="sepa-mandat-membre-select"
              >
                <option value="">— Choisir un membre (ou saisie manuelle) —</option>
                {membres.map((mb) => (
                  <option key={mb.id || mb._id} value={mb.id || mb._id}>
                    {mb.raison_sociale || mb.nom_complet || mb.email}
                  </option>
                ))}
              </select>
              <Input placeholder="Nom / Prénom *" value={form.membre_nom} onChange={(e) => setForm((f) => ({ ...f, membre_nom: e.target.value }))} data-testid="sepa-mandat-nom" />
            </div>
            <Input placeholder="Raison sociale" value={form.raison_sociale} onChange={(e) => setForm((f) => ({ ...f, raison_sociale: e.target.value }))} data-testid="sepa-mandat-raison" />
            <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="sepa-mandat-email" />
            <Input placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))} data-testid="sepa-mandat-tel" />
            <select
              className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white"
              value={form.dispositif}
              onChange={(e) => setForm((f) => ({ ...f, dispositif: e.target.value, libelle_dispositif: '' }))}
              data-testid="sepa-mandat-dispositif"
            >
              {DISPOSITIFS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white"
              value={form.libelle_dispositif}
              onChange={(e) => setForm((f) => ({ ...f, libelle_dispositif: e.target.value }))}
              data-testid="sepa-mandat-libelle-dispositif"
            >
              <option value="">Dispositif précis : automatique (d'après la souscription)</option>
              {(LIBELLES[form.dispositif] || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <Input placeholder="Montant total (€) *" type="number" min="0" step="0.01" value={form.montant_eur} onChange={(e) => setForm((f) => ({ ...f, montant_eur: e.target.value }))} data-testid="sepa-mandat-montant" />
            <Input placeholder="Acompte virement (€)" type="number" min="0" step="0.01" value={form.acompte_eur} onChange={(e) => setForm((f) => ({ ...f, acompte_eur: e.target.value }))} data-testid="sepa-mandat-acompte" />
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="36" value={form.nb_echeances} onChange={(e) => setForm((f) => ({ ...f, nb_echeances: Number(e.target.value) }))} data-testid="sepa-mandat-nb" />
              <span className="text-xs text-slate-500 whitespace-nowrap">éch. ×</span>
              <Input type="number" min="1" value={form.intervalle_jours} onChange={(e) => setForm((f) => ({ ...f, intervalle_jours: Number(e.target.value) }))} data-testid="sepa-mandat-intervalle" />
              <span className="text-xs text-slate-500 whitespace-nowrap">j</span>
            </div>
            <Input type="date" value={form.premiere_date} onChange={(e) => setForm((f) => ({ ...f, premiere_date: e.target.value }))} data-testid="sepa-mandat-date" />
          </div>
          <Button onClick={submit} disabled={busy === 'form'} className="bg-[#0a3d62] hover:bg-[#0c4a78] text-white" data-testid="sepa-mandat-submit">
            {busy === 'form' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {editId ? 'Enregistrer' : 'Créer le mandat + lien de signature'}
          </Button>
        </div>
      )}

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            data-testid="sepa-mandat-filter-status"
          >
            <option value="">Tous</option>
            <option value="en_attente_signature">En attente signature</option>
            <option value="signe">Signés</option>
          </select>
          <select
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            value={filters.famille}
            onChange={(e) => setFilters((f) => ({ ...f, famille: e.target.value, precis: '' }))}
            data-testid="sepa-mandat-filter-famille"
          >
            <option value="">Toutes les familles</option>
            {DISPOSITIFS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {filters.famille && (
            <select
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              value={filters.precis}
              onChange={(e) => setFilters((f) => ({ ...f, precis: e.target.value }))}
              data-testid="sepa-mandat-filter-precis"
            >
              <option value="">Tous les dispositifs {filters.famille}</option>
              {(LIBELLES[filters.famille] || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <Input
            placeholder="Nom, prénom, raison sociale, email, RUM…"
            className="flex-1 min-w-[220px]"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            data-testid="sepa-mandat-search"
          />
          <Input type="date" className="w-36" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="sepa-mandat-from" />
          <Input type="date" className="w-36" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="sepa-mandat-to" />
        </div>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#DEA900] mx-auto" /></div>
        ) : mandats.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6" data-testid="sepa-mandat-empty">Aucun mandat créé manuellement.</p>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-xs text-slate-500 mb-2" data-testid="sepa-mandat-count">{mandats.length} mandat(s)</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-1.5">Membre</th>
                  <th className="px-2 py-1.5">Dispositif</th>
                  <th className="px-2 py-1.5 text-right">Montant</th>
                  <th className="px-2 py-1.5 text-right">Acompte</th>
                  <th className="px-2 py-1.5">Échéances</th>
                  <th className="px-2 py-1.5">Statut</th>
                  <th className="px-2 py-1.5">Créé le</th>
                  <th className="px-2 py-1.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mandats.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 last:border-b-0" data-testid={`sepa-mandat-row-${m.id}`}>
                    <td className="px-2 py-2">
                      <p className="font-semibold text-[#0a3d62]">{m.membre_nom}</p>
                      {m.raison_sociale ? <p className="text-[11px] text-slate-500">{m.raison_sociale}</p> : null}
                      <p className="text-[10px] font-mono text-slate-400">{m.rum}</p>
                    </td>
                    <td className="px-2 py-2">
                      <span className="inline-block px-2 py-0.5 rounded bg-[#0a3d62] text-white text-[10px] font-bold" data-testid={`sepa-mandat-dispositif-precis-${m.id}`}>{m.dispositif_precis || m.dispositif}</span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtEUR(m.montant_cents)}</td>
                    <td className="px-2 py-2 text-right">
                      <span className="font-mono tabular-nums">{fmtEUR(m.acompte_cents)}</span>
                      <span className="block text-[10px] text-slate-400">Virement</span>
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-600">
                      {m.nb_echeances} × {m.intervalle_jours}j
                      <span className="block text-[10px] text-slate-400">1ère : {fmtDate(m.premiere_date)}</span>
                    </td>
                    <td className="px-2 py-2">
                      {m.status === 'signe' ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold" data-testid={`sepa-mandat-status-${m.id}`}>Signé</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded bg-[#DEA900] text-[#0a3d62] text-[10px] font-bold" data-testid={`sepa-mandat-status-${m.id}`}>En attente signature</span>
                      )}
                      {m.stripe_actif ? <span className="block text-[10px] text-emerald-700 mt-0.5">Actif chez Stripe</span> : null}
                      {m.ged_doc_id ? <span className="block text-[10px] text-emerald-700">GED ✓</span> : null}
                      {m.status === 'signe' && (
                        <span className={`block text-[10px] ${m.email_mandat_signe_at ? 'text-slate-500' : 'text-amber-700'}`} data-testid={`sepa-mandat-email-signe-${m.id}`}>
                          {m.email_mandat_signe_at
                            ? `Mandat signé envoyé par e-mail le ${new Date(m.email_mandat_signe_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
                            : 'Mandat signé non envoyé par e-mail'}
                        </span>
                      )}
                      {m.status === 'signe' && m.email_mandat_signe_at && (
                        <span className={`block text-[10px] ${m.email_mandat_signe_ouvert_at ? 'text-emerald-700' : 'text-slate-400'}`} data-testid={`sepa-mandat-email-ouvert-${m.id}`}>
                          {m.email_mandat_signe_ouvert_at
                            ? `Ouvert par le membre le ${new Date(m.email_mandat_signe_ouvert_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
                            : 'Pas encore ouvert (ou images bloquées)'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-500">{fmtDate(m.created_at)}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" title="Voir le mandat" onClick={() => setApercu(m)} className="p-1.5 rounded hover:bg-slate-100 text-[#0a3d62]" data-testid={`sepa-mandat-voir-${m.id}`}><Eye className="h-3.5 w-3.5" /></button>
                        <button type="button" title="Télécharger le mandat" onClick={() => telechargerMandat(`/sepa-stripe/admin/mandats/${m.id}/pdf`, getAuthHeader(), `Mandat_SEPA_${m.rum}.pdf`)} className="p-1.5 rounded hover:bg-slate-100 text-[#0a3d62]" data-testid={`sepa-mandat-telecharger-${m.id}`}><Download className="h-3.5 w-3.5" /></button>
                        <button type="button" title="Documents liés (facture, devis, reçu)" onClick={() => setDocsMandat(m)} className="p-1.5 rounded hover:bg-slate-100 text-[#b88600]" data-testid={`sepa-mandat-documents-${m.id}`}><Paperclip className="h-3.5 w-3.5" /></button>
                        {m.status === 'signe' && (
                          <button type="button" title="Renvoyer le mandat signé par e-mail" onClick={() => doAction(m, 'renvoyer-signe')} disabled={busy === m.id} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" data-testid={`sepa-mandat-renvoyer-signe-${m.id}`}>{busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailCheck className="h-3.5 w-3.5" />}</button>
                        )}
                        {m.status === 'en_attente_signature' && (
                          <>
                            <button type="button" title="Modifier" onClick={() => openEdit(m)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`sepa-mandat-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            <button type="button" title="Renvoyer le lien" onClick={() => doAction(m, 'resend')} disabled={busy === m.id} className="p-1.5 rounded hover:bg-sky-50 text-sky-600" data-testid={`sepa-mandat-resend-${m.id}`}><Send className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                        <button type="button" title="Rafraîchir le statut" onClick={() => doAction(m, 'refresh')} disabled={busy === m.id} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`sepa-mandat-refresh-${m.id}`}>
                          <RefreshCw className={`h-3.5 w-3.5 ${busy === m.id ? 'animate-spin' : ''}`} />
                        </button>
                        {m.status !== 'signe' && (
                          <button type="button" title="Supprimer" onClick={() => doAction(m, 'delete')} disabled={busy === m.id} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`sepa-mandat-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {docsMandat && <MandatDocuments mandat={docsMandat} headers={getAuthHeader()} onClose={() => setDocsMandat(null)} />}
      <MandatPdfApercu mandat={apercu} url={apercu ? `/sepa-stripe/admin/mandats/${apercu.id}/pdf` : ''} headers={getAuthHeader()} onClose={() => setApercu(null)} />
    </div>
  );
}
